import { CurveType } from '@mui/x-charts/models'
import ApiBase from './Api'
import { seriesDropdownItems } from '../pages/companies/company/trades-graph/Toolbar'

const USER_API_PATHS = {
    REQUEST_PASSWORD_RESET_CODE: '/password-code',
    RESET_PASSWORD_WITH_CODE: '/password-reset',
    USER: (userId: string) => `/users/${userId}`,
}

const ADMIN_USER_API_PATHS = {
    ALL_USERS: '/admin/users',
    CREATE_USER: '/admin/users',
    UPDATE_USER: (userId: string) => `/admin/users/${userId}`,
    DELETE_USER: (userId: string) => `/admin/users/${userId}`,
}

export type UserMe = {
    password: {
        currentPassword: string
        newPassword: string
    }
    settings: {
        company: {
            historyOrder: 'asc' | 'desc'
        }
        tradesGraph: {
            curveType?: CurveType
            serie?: (typeof seriesDropdownItems)[number]['id']
        }
        rightsCalculator: {
            amount: number | null
            percentage: number | null
        }
    }
}

class UserApi extends ApiBase {
    async getMe() {
        const user = await this._getRequest(USER_API_PATHS.USER('me'))

        return user as UserMe
    }

    async updateMe(data: Partial<Omit<UserMe, 'settings'>> & { settings: Partial<UserMe['settings']> }) {
        const user = await this._patchRequest(USER_API_PATHS.USER('me'), data)

        return user as UserMe
    }

    async createUser(userData: Record<string, unknown>) {
        return this._postRequest(ADMIN_USER_API_PATHS.CREATE_USER, userData)
    }

    async connectToCustomer(userId: string, customerId: string) {
        return this._patchRequest(ADMIN_USER_API_PATHS.UPDATE_USER(userId), { customerId })
    }

    async disconnectFromCustomer(userId: string) {
        return this._patchRequest(ADMIN_USER_API_PATHS.UPDATE_USER(userId), { customerId: null })
    }

    async adminFindAllUsers() {
        return this._getRequest(ADMIN_USER_API_PATHS.ALL_USERS)
    }

    async adminFindUsersForCustomer(customerId: string) {
        return this._getRequest(ADMIN_USER_API_PATHS.ALL_USERS, { queryParameters: { customerId } })
    }

    async adminDisableUser(userId: string) {
        return this._patchRequest(ADMIN_USER_API_PATHS.UPDATE_USER(userId), { disabled: true })
    }

    async adminDeleteUser(userId: string) {
        return this._deleteRequest(ADMIN_USER_API_PATHS.DELETE_USER(userId))
    }

    async adminEnableUser(userId: string) {
        return this._patchRequest(ADMIN_USER_API_PATHS.UPDATE_USER(userId), { disabled: false })
    }

    async adminSetUserPassword(userId: string, password: string) {
        return this._patchRequest(ADMIN_USER_API_PATHS.UPDATE_USER(userId), { password })
    }

    async adminSetUserCategory(userId: string, userCategory: string) {
        return this._patchRequest(ADMIN_USER_API_PATHS.UPDATE_USER(userId), { userCategory })
    }

    async requestPasswordResetCode(email: string) {
        return this._postRequest(USER_API_PATHS.REQUEST_PASSWORD_RESET_CODE, { email })
    }

    async resetPasswordWithCode(email: string, code: string, newPassword: string) {
        return this._postRequest(USER_API_PATHS.RESET_PASSWORD_WITH_CODE, { email, code, password: newPassword })
    }
}

export default UserApi
